import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  getStepFromStage, 
  getStepOrder, 
  isStepAccessible 
} from '../utils/applicationSteps';

/**
 * Custom hook for managing step-based navigation in the application workflow
 * 
 * This hook provides:
 * - Current step state management
 * - URL synchronization via query parameters
 * - Navigation access control based on application stage
 * - Step completion tracking
 * - Browser history integration
 * 
 * @param {Object} application - The application object containing currentStage
 * @param {string} userRole - Current authenticated user role
 * @returns {Object} Navigation state and functions
 */
const useStepNavigation = (application, userRole) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const prevUrlStepRef = useRef(null);
  const prevStageRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(() => {
    // Initialize from URL parameter if present and accessible
    const urlStep = searchParams.get('step');
    if (urlStep && application?.currentStage) {
      const isPrivilegedDocAccess =
        urlStep === 'documents' && ['ADMIN', 'HR', 'CHAIRMAN'].includes(userRole);
      const isAccessible = isPrivilegedDocAccess || isStepAccessible(urlStep, application.currentStage);
      if (isAccessible) {
        prevUrlStepRef.current = urlStep;
        return urlStep;
      }
    }
    
    // Otherwise, initialize from application.currentStage
    const stageStep = application?.currentStage 
      ? getStepFromStage(application.currentStage)
      : 'applied';
    prevUrlStepRef.current = stageStep;
    return stageStep;
  });

  /**
   * Single effect to handle all synchronization
   */
  useEffect(() => {
    const urlStep = searchParams.get('step');
    const currentStage = application?.currentStage;
    
    // Track if stage changed
    const stageChanged = prevStageRef.current !== currentStage;
    prevStageRef.current = currentStage;
    
    // Track if URL changed
    const urlChanged = prevUrlStepRef.current !== urlStep;
    
    // Case 1: URL changed (user clicked browser back/forward or step button)
    if (urlChanged && urlStep && currentStage) {
      prevUrlStepRef.current = urlStep;
      
      const isPrivilegedDocAccess =
        urlStep === 'documents' && ['ADMIN', 'HR', 'CHAIRMAN'].includes(userRole);
      const isAccessible = isPrivilegedDocAccess || isStepAccessible(urlStep, currentStage);
      
      if (isAccessible && urlStep !== currentStep) {
        setCurrentStep(urlStep);
      } else if (!isAccessible) {
        // URL step is not accessible, reset to valid step
        const validStep = getStepFromStage(currentStage);
        if (validStep !== currentStep) {
          setCurrentStep(validStep);
          prevUrlStepRef.current = validStep;
          setSearchParams({ step: validStep }, { replace: true });
        }
      }
      return;
    }
    
    // Case 2: Stage changed (backend advanced the application)
    if (stageChanged && currentStage) {
      const stageStep = getStepFromStage(currentStage);
      const currentOrder = getStepOrder(currentStep);
      const stageOrder = getStepOrder(stageStep);
      
      // If stage advanced beyond current step, update to new stage
      if (stageOrder > currentOrder && stageStep !== currentStep) {
        setCurrentStep(stageStep);
        prevUrlStepRef.current = stageStep;
        setSearchParams({ step: stageStep }, { replace: true });
      }
      return;
    }
    
    // Case 3: Current step changed programmatically (via navigateToStep)
    if (urlStep !== currentStep) {
      prevUrlStepRef.current = currentStep;
      setSearchParams({ step: currentStep }, { replace: true });
    }
  }, [searchParams, application?.currentStage, currentStep, setSearchParams, userRole]);

  /**
   * Determine if a target step can be accessed
   * Users can access current step and all previous steps
   * 
   * @param {string} targetStepId - The step to check accessibility for
   * @returns {boolean} True if step is accessible
   */
  const canAccessStep = useCallback((targetStepId) => {
    if (!application?.currentStage || !targetStepId) {
      return false;
    }
    
    const isPrivilegedDocAccess =
      targetStepId === 'documents' && ['ADMIN', 'HR', 'CHAIRMAN'].includes(userRole);
    return isPrivilegedDocAccess || isStepAccessible(targetStepId, application.currentStage);
  }, [application?.currentStage, userRole]);

  /**
   * Navigate to a specific step with access control
   * Only allows navigation to accessible steps
   * 
   * @param {string} targetStepId - The step to navigate to
   * @returns {boolean} True if navigation was successful
   */
  const navigateToStep = useCallback((targetStepId) => {
    if (!targetStepId) {
      console.warn('navigateToStep: targetStepId is required');
      return false;
    }

    // Check if step is accessible
    if (!canAccessStep(targetStepId)) {
      console.warn(`navigateToStep: Cannot access step "${targetStepId}"`);
      return false;
    }

    // Update current step (URL will be updated via useEffect)
    setCurrentStep(targetStepId);
    return true;
  }, [canAccessStep]);

  /**
   * Determine if a step has been completed
   * A step is completed if its order is less than the current application stage order
   * 
   * @param {string} stepId - The step to check completion for
   * @returns {boolean} True if step is completed
   */
  const isStepCompleted = useCallback((stepId) => {
    if (!application?.currentStage || !stepId) {
      return false;
    }

    const currentStageStep = getStepFromStage(application.currentStage);
    const stepOrder = getStepOrder(stepId);
    const currentStageOrder = getStepOrder(currentStageStep);

    // A step is completed if it's before the current stage
    return stepOrder < currentStageOrder && stepOrder > 0;
  }, [application?.currentStage]);

  return {
    currentStep,
    canAccessStep,
    navigateToStep,
    isStepCompleted
  };
};

export default useStepNavigation;
